import React, { useEffect, useState } from "react";
import { CompanyTemplate } from "../../types/admin";
import AdminNav from "../../components/AdminNav";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

const AdminCompanyTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<CompanyTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [industryFilter, setIndustryFilter] = useState("");
  const [industries, setIndustries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<CompanyTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, industryFilter]);

  const fetchIndustries = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/api/admin/company-templates/industries`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIndustries(data.data.map((item: any) => item.industry));
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch industries:", err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(industryFilter && { industry: industryFilter }),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/company-templates?${params}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("企業テンプレートの取得に失敗しました");
      }

      const data = await response.json();
      if (data.success) {
        setTemplates(data.data.templates);
        setTotal(data.data.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (template: Partial<CompanyTemplate>) => {
    try {
      const token = localStorage.getItem("auth_token");
      const url = template.id
        ? `${API_BASE_URL}/api/admin/company-templates/${template.id}`
        : `${API_BASE_URL}/api/admin/company-templates`;

      const response = await fetch(url, {
        method: template.id ? "PUT" : "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          industry: template.industry,
          name: template.name,
          rank: template.rank,
          description: template.description || null,
          website: template.website || null,
          average_salary: template.average_salary || null,
        }),
      });

      if (!response.ok) {
        throw new Error("保存に失敗しました");
      }

      alert(template.id ? "更新しました" : "作成しました");
      fetchTemplates();
      fetchIndustries();
      setSelectedTemplate(null);
      setIsCreating(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : "エラーが発生しました");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("この企業テンプレートを削除しますか？")) {
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/api/admin/company-templates/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("削除に失敗しました");
      }

      alert("削除しました");
      fetchTemplates();
    } catch (err) {
      alert(err instanceof Error ? err.message : "エラーが発生しました");
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && templates.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <AdminNav />
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">企業テンプレート管理</h1>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            + 新規作成
          </button>
        </div>

        {/* フィルター */}
        <div className="mb-6 flex gap-3 items-center">
          <label className="text-sm font-medium text-gray-700">業界:</label>
          <select
            value={industryFilter}
            onChange={(e) => {
              setIndustryFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">すべて</option>
            {industries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
          <div className="ml-auto text-sm text-gray-600">総件数: {total}</div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* テンプレートテーブル */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  業界
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  企業名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  ランク
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  平均年収
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.industry}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {template.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.rank}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.average_salary
                      ? `${template.average_salary.toLocaleString()}万円`
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedTemplate(template)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ページネーション */}
        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            前へ
          </button>
          <span className="text-sm text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            次へ
          </button>
        </div>

        {/* 作成・編集モーダル */}
        {(selectedTemplate || isCreating) && (
          <TemplateModal
            template={selectedTemplate}
            industries={industries}
            onClose={() => {
              setSelectedTemplate(null);
              setIsCreating(false);
            }}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </>
  );
};

interface TemplateModalProps {
  template: CompanyTemplate | null;
  industries: string[];
  onClose: () => void;
  onSubmit: (template: Partial<CompanyTemplate>) => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  template,
  industries,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    industry: template?.industry || "",
    name: template?.name || "",
    rank: template?.rank || 1,
    description: template?.description || "",
    website: template?.website || "",
    average_salary: template?.average_salary || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(template && { id: template.id }),
      industry: formData.industry,
      name: formData.name,
      rank: formData.rank,
      description: formData.description || null,
      website: formData.website || null,
      average_salary: formData.average_salary
        ? Number(formData.average_salary)
        : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {template ? "企業テンプレート編集" : "企業テンプレート作成"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              業界 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) =>
                setFormData({ ...formData, industry: e.target.value })
              }
              list="industries"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: IT・通信"
            />
            <datalist id="industries">
              {industries.map((industry) => (
                <option key={industry} value={industry} />
              ))}
            </datalist>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              企業名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: 株式会社〇〇"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ランク <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.rank}
              onChange={(e) =>
                setFormData({ ...formData, rank: Number(e.target.value) })
              }
              required
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="企業の説明"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ウェブサイト
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) =>
                setFormData({ ...formData, website: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              平均年収（万円）
            </label>
            <input
              type="number"
              value={formData.average_salary}
              onChange={(e) =>
                setFormData({ ...formData, average_salary: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: 600"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCompanyTemplates;
